import React, { useMemo } from "react"
import { useDevice } from "hooks/useDevice"
import PropTypes from "prop-types"
import Spinner from "components/common/Spinner"

import moment from "moment"

import { useSelector } from "react-redux"
import styles from "../../../Calendar.module.css"
import DayCell from "./DayCell"

const DAY_KEY_FORMAT = "YYYY-MM-DD"
const dayKey = (m) => m.format(DAY_KEY_FORMAT)

const Month = ({ date, onChangeFilters }) => {
  const { loading } = useSelector((state) => state.calendar)
  const { calendarTimeOff } = useSelector((state) => state.calendar)

  const monthStartDate = useMemo(() => moment(date).startOf("month"), [date])
  const monthEndDate = useMemo(() => moment(date).endOf("month"), [date])

  // NEW: Calculate the start and end of the *full grid*
  const gridStartDate = useMemo(() => moment(date).startOf("month").startOf("week"), [date])
  const gridEndDate = useMemo(() => moment(date).endOf("month").endOf("week"), [date])

  // UPDATED: Count days for the full grid
  const calendarDaysCount = gridEndDate.diff(gridStartDate, "days") + 1

  // UPDATED: Generate days for the full grid
  const calendarDays = useMemo(
    () => new Array(calendarDaysCount).fill().map((_, index) => gridStartDate.clone().add(index, "days")),
    [gridStartDate, calendarDaysCount]
  )

  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet

  // FIXED: Generate week day names correctly, independent of the month's start day.
  // This will always be [Sun, Mon, Tue, Wed, Thu, Fri, Sat] (or locale equivalent)
  const weekDayNames = useMemo(
    () => new Array(7).fill().map((_, index) => moment().day(index).format("ddd")),
    [] // No dependency needed
  )

  // Normalize time off per day for this month view using dummyTimeOff
  const timeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = dayKey(day)
      const items = (calendarTimeOff || [])
        .filter((toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day"))
        .map((toff) => {
          const start = moment(toff.start_time, ["h:mm A", "hh:mm A", "HH:mm"])?.format("h:mm A")
          const end = moment(toff.end_time, ["h:mm A", "hh:mm A", "HH:mm"])?.format("h:mm A")
          return `${start} - ${end}`
        })
      acc[dayStr] = items
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  // Detect if any time off for the day is all-day
  const allDayTimeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const isAllDay = calendarTimeOff?.some(
        (toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day") && Boolean(toff.all_day)
      )
      acc[dayKey(day)] = isAllDay || false
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  return (
    <table className={styles.monthTable}>
      <thead>
        <tr className={styles.monthHeader}>
          {weekDayNames.map((dayName) => (
            <td key={dayName} className={styles.weekDayName}>
              {isCompact ? dayName[0] : dayName}
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, rowIdx) => {
          // The slice will now always have 7 days, so the key will be stable
          const weekDays = calendarDays.slice(rowIdx * 7, rowIdx * 7 + 7)
          const weekKey = weekDays.map((day) => dayKey(day)).join("-")
          return (
            <tr key={weekKey}>
              {weekDays?.map((day, colIdx) => {
                const isPastDay = day.isBefore(moment().startOf("day"), "day")
                return (
                  <td
                    key={dayKey(day)}
                    className={`${styles.monthTableCell} ${
                      isPastDay ? styles.pastDay : ""
                    } border-gray-lightest p-2 border-end border-bottom`}
                  >
                    <DayCell
                      day={day}
                      // This prop correctly handles styling for days not in the current month
                      thisMonth={day.isBetween(monthStartDate, monthEndDate, "day", "[]")}
                      lastCol={colIdx === 6}
                      lastRow={rowIdx === Math.ceil(calendarDays.length / 7) - 1}
                      monthStartDate={dayKey(monthStartDate)}
                      monthEndDate={dayKey(monthEndDate)}
                      timeOffRanges={timeOffByDay[dayKey(day)] || []}
                      allDayTimeOff={allDayTimeOffByDay[dayKey(day)]}
                      onChangeFilters={onChangeFilters}
                    />
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
      {loading && (
        <tfoot>
          <tr>
            <td colSpan={7}>
              <Spinner className={styles.spinner} absolute />
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  )
}

Month.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onChangeFilters: PropTypes.func.isRequired
}
export default Month
